/**
 * Shared logic for evaluating visibility conditions across the flow engine.
 */

import type { FlowCondition } from './types'

/**
 * Evaluates a single condition against a values object.
 * @param field - Key of the field to evaluate
 * @param operator - Comparison operator (eq, neq, in, notIn)
 * @param value - Expected value(s)
 * @param allValues - All form values to check against
 * @returns true if the condition is satisfied
 */
export function evaluateCondition(
  field: string,
  operator: string,
  value: string | string[] | boolean | number,
  allValues: Record<string, unknown>
): boolean {
  const current = allValues[field]

  switch (operator) {
    case 'eq':
      return current === value
    case 'neq':
      return current !== value
    case 'in':
      return Array.isArray(value) && value.includes(String(current ?? ''))
    case 'notIn':
      return Array.isArray(value) && !value.includes(String(current ?? ''))
    default:
      return true
  }
}

/**
 * Evaluates a FlowCondition object against a values object.
 */
export function evaluateFlowCondition(
  condition: FlowCondition,
  allValues: Record<string, unknown>
): boolean {
  return evaluateCondition(condition.field, condition.operator, condition.value, allValues)
}

/**
 * Evaluates a single visibility condition.
 */
export function evaluateSingleCondition(
  condition: FlowCondition | undefined,
  allValues: Record<string, unknown>
): boolean {
  return condition ? evaluateFlowCondition(condition, allValues) : true
}

/**
 * Evaluates a list of conditions with AND logic (all must be true).
 */
export function evaluateAllConditions(
  conditions: FlowCondition[] | undefined,
  allValues: Record<string, unknown>
): boolean {
  return conditions?.every((c) => evaluateFlowCondition(c, allValues)) ?? true
}

/**
 * Evaluates a list of condition groups with OR logic (any group must satisfy all conditions).
 */
export function evaluateAnyConditionGroup(
  groups: FlowCondition[][] | undefined,
  allValues: Record<string, unknown>
): boolean {
  return groups
    ? groups.some((group) => group.every((c) => evaluateFlowCondition(c, allValues)))
    : true
}
